/**
 * Утилита для работы с IndexedDB
 * Используется для хранения больших объемов данных (fileTree, branches cache и т.д.)
 */

const DB_NAME = 'GlobusContextDB';
const DB_VERSION = 1;
const STORE_NAME = 'storage';

class IndexedDBStorage {
  constructor() {
    this.db = null;
    this.initPromise = this.init();
  }

  /**
   * Инициализация базы данных
   */
  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('Ошибка открытия IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;

        // Браузер закрыл соединение (нехватка ресурсов, idle и т.д.)
        this.db.onclose = () => {
          this.db = null;
          this.initPromise = this.init();
        };

        // Другая вкладка открыла БД с новой версией — нужно отпустить соединение
        this.db.onversionchange = () => {
          this.db.close();
          this.db = null;
          this.initPromise = this.init();
        };

        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Создаём хранилище, если его нет
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      };
    });
  }

  /**
   * Убедиться что соединение с БД активно, переподключиться если нет
   */
  async ensureConnection() {
    await this.initPromise;
    if (!this.db) {
      this.initPromise = this.init();
      await this.initPromise;
    }
  }

  /**
   * Выполнить операцию с транзакцией, retry при закрытом соединении
   */
  async withTransaction(mode, callback) {
    const maxRetries = 2;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      await this.ensureConnection();
      try {
        const transaction = this.db.transaction([STORE_NAME], mode);
        const store = transaction.objectStore(STORE_NAME);
        return await callback(store);
      } catch (error) {
        const isConnectionClosing =
          error?.name === 'InvalidStateError' || error?.message?.includes('connection is closing');
        if (isConnectionClosing && attempt < maxRetries) {
          this.db = null;
          this.initPromise = this.init();
          continue;
        }
        throw error;
      }
    }
  }

  /**
   * Получить данные по ключу
   */
  async getItem(key) {
    try {
      return await this.withTransaction('readonly', (store) => {
        return new Promise((resolve, reject) => {
          const request = store.get(key);
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        });
      });
    } catch (error) {
      console.error('Ошибка getItem:', error);
      return null;
    }
  }

  /**
   * Сохранить данные по ключу
   */
  async setItem(key, value) {
    try {
      return await this.withTransaction('readwrite', (store) => {
        return new Promise((resolve, reject) => {
          const request = store.put(value, key);
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
      });
    } catch (error) {
      console.error('Ошибка setItem:', error);
      throw error;
    }
  }

  /**
   * Удалить данные по ключу
   */
  async removeItem(key) {
    try {
      return await this.withTransaction('readwrite', (store) => {
        return new Promise((resolve, reject) => {
          const request = store.delete(key);
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
      });
    } catch (error) {
      console.error('Ошибка removeItem:', error);
    }
  }

  /**
   * Очистить всё хранилище
   */
  async clear() {
    try {
      return await this.withTransaction('readwrite', (store) => {
        return new Promise((resolve, reject) => {
          const request = store.clear();
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
      });
    } catch (error) {
      console.error('Ошибка clear:', error);
    }
  }

  /**
   * Получить все ключи
   */
  async getAllKeys() {
    try {
      return await this.withTransaction('readonly', (store) => {
        return new Promise((resolve, reject) => {
          const request = store.getAllKeys();
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        });
      });
    } catch (error) {
      console.error('Ошибка getAllKeys:', error);
      return [];
    }
  }
}

// Создаём единственный экземпляр
const indexedDBStorage = new IndexedDBStorage();

export default indexedDBStorage;
